import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2Icon, AlertTriangleIcon } from 'lucide-react';
import { landingPagesApi, LandingPageRecord } from '../services/creatorApi';
type PageState = 'loading' | 'ready' | 'not_found' | 'error';
export function PublicLandingPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [state, setState] = useState<PageState>('loading');
  const [page, setPage] = useState<LandingPageRecord | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (!slug) {
      setState('not_found');
      return;
    }
    const loadPage = async () => {
      setState('loading');
      try {
        const record = await landingPagesApi.findBySlug(slug);
        if (!record) {
          setState('not_found');
          return;
        }
        if (record.Status?.toLowerCase() !== 'active') {
          setState('not_found');
          return;
        }
        setPage(record);
        setState('ready');
      } catch (err) {
        console.error('Failed to load landing page:', err);
        setState('error');
      }
    };
    loadPage();
  }, [slug]);
  // Build the full HTML document for the iframe
  const buildDocument = (record: LandingPageRecord): string => {
    const css = record.CSS || '';
    const js = record.JavaScript1 || '';
    const html = record.HTML || '';
    const parts: string[] = [];
    parts.push('<!DOCTYPE html>');
    parts.push('<html lang="en">');
    parts.push('<head>');
    parts.push('<meta charset="utf-8">');
    parts.push('<meta name="viewport" content="width=device-width, initial-scale=1">');
    if (record.Page_Name) {
      parts.push(`<title>${escapeHtml(record.Page_Name)}</title>`);
    }
    parts.push('<style>{`');
    parts.push('*, *::before, *::after { box-sizing: border-box; }');
    parts.push('body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }');
    if (css) {
      parts.push(css);
    }
    parts.push('`}</style>');
    parts.push('</head>');
    parts.push('<body>');
    parts.push(html);
    if (js) {
      parts.push(`<script>${js}<\/script>`);
    }
    parts.push('</body>');
    parts.push('</html>');
    return parts.join('\n');
  };
  if (state === 'loading') {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-3804">
        <div className="text-center" data-id="element-3805">
          <Loader2Icon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-3" data-id="element-3806" />
          <p className="text-sm text-gray-500" data-id="element-3807">Loading...</p>
        </div>
      </div>;
  }
  if (state === 'not_found') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-3808">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-3809">
          <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-6" data-id="element-3810">
            <AlertTriangleIcon className="h-8 w-8 text-amber-500" data-id="element-3811" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3812">
            Page not found
          </h1>
          <p className="text-gray-500 text-sm" data-id="element-3813">
            The page you're looking for doesn't exist or is no longer available.
          </p>
        </div>
      </div>;
  }
  if (state === 'error') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-3814">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-3815">
          <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6" data-id="element-3816">
            <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-3817" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3818">
            Something went wrong
          </h1>
          <p className="text-gray-500 text-sm mb-4" data-id="element-3819">
            We couldn't load this page right now.
          </p>
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-3820">
            Try Again
          </button>
        </div>
      </div>;
  }
  // Ready state — render the landing page in a full-screen iframe
  if (page) {
    const doc = buildDocument(page);
    return <iframe ref={iframeRef} srcDoc={doc} title={page.Page_Name || 'Landing Page'} className="w-full border-0" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-id="element-3821" />;
  }
  return null;
}
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}